import { ReactNode, useState } from 'react';
import { Info, X } from 'lucide-react';

/**
 * Reusable first-run guidance callout (F-022). Renders a concise
 * "what this is / how to start" intro at the top of a complex tool.
 *
 * Dismissal is persisted in localStorage under a stable per-tool key,
 * so once a user dismisses it they never see it again on that device.
 * Keep `body` to ~one paragraph and route all copy through i18n at the
 * call site.
 */
export function IntroCallout({
  storageKey,
  title,
  body,
  cta,
}: {
  /** Stable key, e.g. `intro.matching`. Persisted as `plyez.intro.<storageKey>`. */
  storageKey: string;
  title: string;
  body: string;
  cta?: { label: string; onClick: () => void } | { label: string; href: string };
}) {
  const lsKey = `plyez.intro.${storageKey}`;
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(lsKey) === '1';
  });

  if (dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    if (typeof window !== 'undefined') window.localStorage.setItem(lsKey, '1');
  };

  let ctaNode: ReactNode = null;
  if (cta) {
    const cls =
      'mt-2 inline-flex items-center text-xs font-medium text-accent-700 dark:text-accent-300 hover:underline';
    ctaNode =
      'href' in cta ? (
        <a href={cta.href} className={cls}>
          {cta.label}
        </a>
      ) : (
        <button type="button" onClick={cta.onClick} className={cls}>
          {cta.label}
        </button>
      );
  }

  return (
    <div className="mb-5 flex items-start gap-3 rounded-lg border border-accent-200 dark:border-accent-800 bg-accent-50/60 dark:bg-accent-900/20 px-4 py-3">
      <Info size={16} className="mt-0.5 shrink-0 text-accent-600 dark:text-accent-300" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-ink-900 dark:text-ink-100">{title}</div>
        <p className="mt-0.5 text-xs leading-relaxed text-ink-600 dark:text-ink-300">{body}</p>
        {ctaNode}
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="shrink-0 rounded-md p-1 text-ink-400 hover:text-ink-700 dark:hover:text-ink-200"
      >
        <X size={14} />
      </button>
    </div>
  );
}
